import React, { useState, useEffect } from 'react';
import OrderForm from './OrderForm';
import { supabase } from './supabaseClient';
import axios from 'axios';
import TopMenuBar from './TopMenuBar';
import addCreditsSystem from './add_credits_system';

const profileFields = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'address', label: 'Address' },
  { key: 'address2', label: 'Address Line 2' },
  { key: 'phone', label: 'Phone' },
  { key: 'delivery_instructions', label: 'Delivery Instructions' },
];

const MAPBOX_TOKEN = "pk.eyJ1IjoieXVraWhpZG8iLCJhIjoiY21jbzdsa2o0MTM1dzJscTF6ZTJjbzBxbyJ9.0oahdC5gR_i8GTyn1zWtwQ";

// Helper component to display a receipt image with signed URL
function ReceiptImage({ path }) {
  const [signedUrl, setSignedUrl] = useState(null);
  useEffect(() => {
    async function getSignedUrl() {
      if (!path) return;
      // Remove 'screenshots/' prefix if present
      const cleanPath = path.replace(/^screenshots\//, '').replace(/^screenshots\//, '');
      const { data, error } = await supabase.storage.from('screenshots').createSignedUrl(cleanPath, 60 * 60); // 1 hour
      if (data?.signedUrl) setSignedUrl(data.signedUrl);
      else setSignedUrl(null);
    }
    getSignedUrl();
  }, [path]);
  if (!path) return null;
  return (
    <img src={signedUrl || ''} alt="DoorDash Receipt" className="screenshot-preview" style={{maxWidth: '100%', borderRadius: '4px'}} />
  );
}

export default function Dashboard({ user }) {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('placeOrder');
  const [profile, setProfile] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileMsg, setProfileMsg] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [addCreditsMsg, setAddCreditsMsg] = useState('');
  const [addCreditsLoading, setAddCreditsLoading] = useState(false);
  const [addCreditsStep, setAddCreditsStep] = useState(1);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [addCreditsCodeInfo, setAddCreditsCodeInfo] = useState(null);
  const [addCreditsPolling, setAddCreditsPolling] = useState(false);
  const [addCreditsStatus, setAddCreditsStatus] = useState('pending');
  const API_BASE = 'http://localhost:5000';
  const [showSplash, setShowSplash] = useState(true);
  const [splashText, setSplashText] = useState('CLICKYBOIS');
  const [splashVisible, setSplashVisible] = useState(true);

  // Add new states for verification
  const [verificationStatus, setVerificationStatus] = useState('pending'); // pending, checking, verified, failed
  const [verificationMessage, setVerificationMessage] = useState('');

  useEffect(() => {
    if (showSplash) {
      setSplashVisible(true);
      const timeout1 = setTimeout(() => {
        setSplashText(`Welcome, ${profile?.name ? profile.name : user.email || ''}`);
      }, 1200);
      const timeout2 = setTimeout(() => {
        setSplashVisible(false);
      }, 2200);
      const timeout3 = setTimeout(() => {
        setShowSplash(false);
      }, 2700);
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    }
  }, [showSplash, profile, user]);

  useEffect(() => {
    // Fetch user's past orders
    const fetchOrders = async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      console.log('Fetched orders:', data, 'Error:', error); // Debug log
      console.log('Current user id:', user.id);
      setOrders(data || []);
    };
    fetchOrders();
  }, [user.id]);

  useEffect(() => {
    // Fetch user profile info
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setProfile(data || {});
      setProfileForm(data || {});
    };
    fetchProfile();
  }, [user.id]);

  const handleProfileChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handleProfileSave = async () => {
    setProfileMsg('');
    const { error } = await supabase.from('users').upsert([
      { id: user.id, ...profileForm, email: profileForm.email || user.email }
    ]);
    if (error) {
      setProfileMsg(error.message);
    } else {
      setProfileMsg('Profile updated!');
      setEditProfile(false);
      setProfile(profileForm);
    }
  };

  const handleOrderPlaced = async () => {
    const { data } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setOrders(data || []);
    setActiveTab('orders');
  };

  // Mapbox address autocomplete handler
  const handleAddressChange = async (e) => {
    const value = e.target.value;
    setProfileForm({ ...profileForm, address: value });
    if (!value) {
      setAddressSuggestions([]);
      return;
    }
    setAddressLoading(true);
    try {
      const res = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json`,
        {
          params: {
            access_token: MAPBOX_TOKEN,
            autocomplete: true,
            country: 'US',
            limit: 5,
          },
        }
      );
      setAddressSuggestions(res.data.features.map(f => f.place_name));
    } catch (err) {
      setAddressSuggestions([]);
    }
    setAddressLoading(false);
  };

  const handleAddressSelect = (suggestion) => {
    setProfileForm({ ...profileForm, address: suggestion });
    setAddressSuggestions([]);
  };

  // Add credits handler (CashApp flow)
  const handleAddCredits = async () => {
    try {
      const code = addCreditsSystem.generateUniqueCode();
      console.log('Generated unique code:', code);
      setGeneratedCode(code);
      setAddCreditsStep(2); // Move to payment instructions
    } catch (error) {
      console.error('Error generating code:', error);
      alert('Failed to generate code. Please try again.');
    }
  };

  // Update the payment confirmation handler
  const handlePaymentConfirm = async () => {
    console.log('Payment confirmation started with:', {
      generatedCode,
      addCreditsAmount,
      userId: user.id,
      currentStatus: verificationStatus
    });

    try {
      setVerificationStatus('checking');
      setVerificationMessage('Checking for your payment...');
      
      console.log('Calling verifyPayment...');
      const result = await addCreditsSystem.verifyPayment(
        generatedCode,
        parseFloat(addCreditsAmount),
        user.id
      );
      console.log('Verification result:', result);
      
      if (result.verified) {
        console.log('Payment verified successfully');
        setVerificationStatus('verified');
        setVerificationMessage('Payment received! Credits have been added to your account.');
        // Auto close after 3 seconds
        setTimeout(() => {
          console.log('Auto-closing payment modal');
          setShowAddCreditsModal(false);
          setAddCreditsStep(1);
          setGeneratedCode(null);
          setVerificationStatus('pending');
          setVerificationMessage('');
        }, 3000);
      } else {
        console.log('Payment verification failed:', result.message);
        setVerificationStatus('failed');
        setVerificationMessage(result.message || 'Payment not found. Please try again in a few moments.');
      }
    } catch (error) {
      console.error('Payment confirmation error:', {
        error,
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setVerificationStatus('failed');
      setVerificationMessage('Error checking payment. Please try again.');
    }
  };

  // Remove the polling effect since we're rebuilding
  useEffect(() => {
    // Empty for now - we'll rebuild this
  }, [addCreditsPolling, addCreditsCodeInfo, user.id]);

  // Helper to get street address only
  const streetAddress = profile?.address ? profile.address.split('\n')[0] : '';

  return (
    <>
      {showSplash && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.92)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: splashVisible ? 1 : 0,
          transition: 'opacity 0.7s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <span style={{
            color: '#fff',
            fontSize: '2.5rem',
            fontWeight: 700,
            letterSpacing: '0.25em',
            fontFamily: 'Satoshi, Rajdhani, Inter, Arial, sans-serif',
            textAlign: 'center',
            opacity: splashVisible ? 1 : 0,
            transition: 'opacity 0.7s cubic-bezier(0.4,0,0.2,1)',
          }}>{splashText}</span>
        </div>
      )}
      <TopMenuBar
        address={streetAddress}
        credits={profile?.credits}
        onEditAddress={() => setShowAddressModal(true)}
        onAddCredits={() => setShowAddCreditsModal(true)}
      />
      <div style={{ marginBottom: 32 }} />
      <div style={{
        paddingTop: 0,
        background: 'rgba(30, 30, 30, 0.55)',
        color: '#ffe082',
        borderRadius: 14,
        boxShadow: '0 6px 32px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        padding: '36px 28px 28px 28px',
        maxWidth: 700,
        margin: '0 auto'
      }}>
        <div className="dashboard-main-flex" style={{display: 'flex', gap: 32, justifyContent: 'flex-start', alignItems: 'flex-start', maxWidth: 1200, margin: '0 auto'}}>
          {/* Address & Delivery Modal */}
          {showAddressModal && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.35)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}} onClick={() => setShowAddressModal(false)}>
              <div className="modal-content" style={{background:'#23272f', borderRadius:14, padding:'36px 28px 28px 28px', minWidth:320, maxWidth:'95vw', boxShadow:'0 6px 32px rgba(0,0,0,0.12)', position:'relative'}} onClick={e => e.stopPropagation()}>
                <button style={{position:'absolute', top:12, right:18, background:'none', border:'none', color:'#fff', fontSize:24, cursor:'pointer'}} onClick={() => setShowAddressModal(false)}>&times;</button>
                <h3 style={{marginTop:0, color:'#fff'}}>Edit Address & Delivery</h3>
                <div className="form-group" style={{position:'relative'}}>
                  <label htmlFor="address">Address</label>
                  <input
                    id="address"
                    name="address"
                    value={profileForm.address || ''}
                    onChange={handleAddressChange}
                    autoComplete="off"
                  />
                  {addressLoading && <div style={{color:'#aaa', fontSize:'0.95em'}}>Loading...</div>}
                  {addressSuggestions.length > 0 && (
                    <ul style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      background: '#23272f',
                      border: '1px solid #444',
                      borderRadius: 6,
                      zIndex: 10,
                      listStyle: 'none',
                      margin: 0,
                      padding: 0,
                      maxHeight: 180,
                      overflowY: 'auto',
                    }}>
                      {addressSuggestions.map((s, i) => (
                        <li
                          key={i}
                          style={{padding: '8px 12px', cursor: 'pointer'}}
                          onClick={() => handleAddressSelect(s)}
                          onMouseDown={e => e.preventDefault()}
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="address2">Address Line 2</label>
                  <input id="address2" name="address2" value={profileForm.address2 || ''} onChange={handleProfileChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="delivery_instructions">Delivery Instructions</label>
                  <textarea id="delivery_instructions" name="delivery_instructions" value={profileForm.delivery_instructions || ''} onChange={handleProfileChange} rows={2} style={{resize:'vertical'}} />
                </div>
                <div style={{display:'flex', gap:12, marginTop:10}}>
                  <button onClick={async () => { await handleProfileSave(); setShowAddressModal(false); }} style={{background: 'transparent', color: '#fff', border: '1px solid #fff', borderRadius:8, padding:'10px 0', fontSize:'1.08em', cursor:'pointer', flex:1}}>Save</button>
                  <button style={{background: 'transparent', color: '#bbb', border: '1px solid #bbb', borderRadius:8, padding:'10px 0', fontSize:'1.08em', cursor:'pointer', flex:1}} onClick={() => { setShowAddressModal(false); setProfileForm(profile); setProfileMsg(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {/* Add Credits Modal */}
          {showAddCreditsModal && (
            <div className="modal-overlay" style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.35)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}} onClick={() => setShowAddCreditsModal(false)}>
              <div className="modal-content" style={{background:'#23272f', borderRadius:14, padding:'36px 28px 28px 28px', minWidth:320, maxWidth:'95vw', boxShadow:'0 6px 32px rgba(0,0,0,0.12)', position:'relative'}} onClick={e => e.stopPropagation()}>
                <button 
                  style={{position:'absolute', top:12, right:18, background:'none', border:'none', color:'#fff', fontSize:24, cursor:'pointer'}} 
                  onClick={() => {
                    setShowAddCreditsModal(false);
                    setAddCreditsStep(1); // Reset step when closing
                    setGeneratedCode(null); // Clear code
                  }}
                >&times;</button>
                
                <h3 style={{marginTop:0, color:'#fff'}}>Add Credits</h3>
                
                {addCreditsStep === 1 ? (
                  <div>
                    <div className="form-group">
                      <label htmlFor="addCreditsAmount">Amount</label>
                      <input 
                        id="addCreditsAmount" 
                        type="number" 
                        min="1" 
                        step="0.01"
                        value={addCreditsAmount} 
                        onChange={e => setAddCreditsAmount(e.target.value)} 
                        placeholder="0.00"
                      />
                    </div>
                    <button 
                      onClick={handleAddCredits} 
                      style={{
                        background: 'none',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: 8,
                        padding: '10px 0',
                        fontSize: '1.08em',
                        cursor: 'pointer',
                        width: '100%',
                        marginTop: 12
                      }}
                    >
                      Generate Payment Code
                    </button>
                  </div>
                ) : (
                  <div style={{color: '#fff'}}>
                    <div style={{
                      background: 'rgba(255,255,255,0.1)',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{fontSize: '1.2em', marginBottom: '8px'}}>Your Payment Code:</div>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: '2em',
                        color: '#ffe082',
                        letterSpacing: '2px',
                        marginBottom: '8px'
                      }}>
                        {generatedCode}
                      </div>
                      <div style={{color: '#ffe082', fontSize: '1.2em'}}>
                        Amount: ${addCreditsAmount}
                      </div>
                    </div>

                    <div style={{marginBottom: '20px'}}>
                      <div style={{fontWeight: 'bold', marginBottom: '10px'}}>Payment Instructions:</div>
                      <ol style={{paddingLeft: '20px', margin: 0}}>
                        <li style={{marginBottom: '8px'}}>Open CashApp</li>
                        <li style={{marginBottom: '8px'}}>Send ${addCreditsAmount} to <span style={{color: '#ffe082'}}>$clickybois</span></li>
                        <li style={{marginBottom: '8px'}}>Add your code <span style={{color: '#ffe082'}}>{generatedCode}</span> in the payment note</li>
                      </ol>
                    </div>

                    <div style={{
                      textAlign: 'center',
                      marginBottom: '20px',
                      background: 'rgba(255,255,255,0.05)',
                      padding: '20px',
                      borderRadius: '12px'
                    }}>
                      <img
                        src="/$clickybois.png"
                        alt="CashApp QR Code"
                        style={{
                          width: '200px',
                          height: '200px',
                          display: 'block',
                          margin: '0 auto',
                          borderRadius: '12px'
                        }}
                      />
                      <div style={{
                        marginTop: '10px',
                        color: '#ffe082',
                        fontSize: '0.9em'
                      }}>
                        Scan to pay with CashApp
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(255,224,130,0.1)',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '0.9em',
                      textAlign: 'center'
                    }}>
                      {verificationStatus === 'checking' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                          <div className="loading-spinner" style={{
                            width: '30px',
                            height: '30px',
                            border: '3px solid #ffe082',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}>
                            <style>{`
                              @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                              }
                            `}</style>
                          </div>
                          <div style={{ color: '#ffe082' }}>{verificationMessage}</div>
                        </div>
                      ) : verificationStatus === 'verified' ? (
                        <div style={{ color: '#4caf50' }}>{verificationMessage}</div>
                      ) : verificationStatus === 'failed' ? (
                        <div style={{ color: '#ff4757' }}>{verificationMessage}</div>
                      ) : (
                        'Your credits will be added automatically after payment is confirmed'
                      )}
                      </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      marginTop: '20px'
                    }}>
                      <button 
                        onClick={handlePaymentConfirm}
                        disabled={verificationStatus === 'checking'}
                        style={{
                          background: verificationStatus === 'checking' ? '#666' : '#ffe082',
                          color: verificationStatus === 'checking' ? '#aaa' : '#23272f',
                          border: 'none',
                          borderRadius: 8,
                          padding: '12px 0',
                          fontSize: '1.1em',
                          fontWeight: '600',
                          cursor: verificationStatus === 'checking' ? 'not-allowed' : 'pointer',
                          width: '100%',
                          transition: 'all 0.2s ease',
                          opacity: verificationStatus === 'checking' ? 0.7 : 1
                        }}
                      >
                        {verificationStatus === 'checking' ? 'Checking...' : "I've Paid"}
                      </button>

                      <button 
                        onClick={() => {
                          setShowAddCreditsModal(false);
                          setAddCreditsStep(1);
                          setGeneratedCode(null);
                          setVerificationStatus('pending');
                          setVerificationMessage('');
                        }}
                        disabled={verificationStatus === 'checking'}
                        style={{
                          background: 'none',
                          color: '#fff',
                          border: '1px solid #fff',
                          borderRadius: 8,
                          padding: '10px 0',
                          fontSize: '1em',
                          cursor: verificationStatus === 'checking' ? 'not-allowed' : 'pointer',
                          width: '100%',
                          opacity: verificationStatus === 'checking' ? 0.5 : 1
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Main panel (tab content) */}
          <div style={{maxWidth: 600, flex: 1, margin: '0 auto'}}>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              {/* Remove the welcome message heading */}
            </div>
            <div style={{ display: 'flex', gap: 18, marginBottom: 28, justifyContent: 'center' }}>
              <button
                className={activeTab === 'placeOrder' ? 'active-tab' : ''}
                style={{ fontWeight: 600, fontFamily: 'Rajdhani', fontSize: '1.1rem', background: 'none', border: 'none', color: activeTab === 'placeOrder' ? '#fff' : '#fff', cursor: 'pointer', padding: '8px 18px', borderBottom: activeTab === 'placeOrder' ? '2.5px solid #fff' : '2.5px solid transparent', borderRadius: 0 }}
                onClick={() => setActiveTab('placeOrder')}
              >
                Place Order
              </button>

            </div>
            {activeTab === 'orders' && (
              <>
                <h3 style={{marginTop: '2em', color: '#fff'}}>Your Orders</h3>
                <ul style={{paddingLeft: 0, listStyle: 'none'}}>
                  {orders.length === 0 && <li style={{color:'#fff'}}>No orders yet.</li>}
                  {orders.map(order => (
                    <li key={order.id} style={{marginBottom: '1em', background: '#23272f', borderRadius: 8, padding: 12}}>
                      <div>
                        <strong style={{color: '#fff'}}>Status:</strong> {order.status === 'order complete' ? (
                          <span style={{color:'#4caf50'}}>Order Complete</span>
                        ) : order.status === 'order placed, waiting to be fulfilled' ? (
                          <span style={{color:'#fff'}}>Waiting to be fulfilled</span>
                        ) : order.status === 'pending_payment' ? (
                          <span style={{color:'#fff'}}>Pending Payment</span>
                        ) : (
                          <span style={{color:'#fff'}}>{order.status}</span>
                        )}
                      </div>
                      {order.group_order_link && (
                        <div style={{marginTop:8, marginBottom:8}}>
                          <strong style={{color: '#fff'}}>Track your order:</strong><br/>
                          <a href={order.group_order_link} target="_blank" rel="noopener noreferrer" style={{color:'#90caf9', wordBreak:'break-all'}}>{order.group_order_link}</a>
                        </div>
                      )}
                      <div><strong style={{color: '#fff'}}>Total:</strong> <span style={{color: '#fff'}}>${order.total || '0.00'}</span></div>
                      <div><strong style={{color: '#fff'}}>Placed:</strong> <span style={{color: '#fff'}}>{new Date(order.created_at).toLocaleString()}</span></div>
                      {/* Display cart images */}
                      {order.doordash_cart_url ? (
                        <div style={{marginTop: 8}}>
                          {/* Parse cart URLs from JSON or use as single string */}
                          {(() => {
                            let cartUrls = [];
                            try {
                              const parsed = JSON.parse(order.doordash_cart_url);
                              if (Array.isArray(parsed)) {
                                cartUrls = parsed;
                              } else {
                                cartUrls = [order.doordash_cart_url];
                              }
                            } catch (e) {
                              cartUrls = [order.doordash_cart_url];
                            }
                            
                            return (
                              <>
                                <div style={{fontSize: '0.9em', color: '#fff', marginBottom: 4}}>
                                  Shopping Cart {cartUrls.length > 1 ? `(${cartUrls.length} images)` : ''}:
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                  {cartUrls.map((cartUrl, index) => (
                                    <img 
                                      key={index}
                                      src={`https://lazzdyaxyzqrzmmucwhv.supabase.co/storage/v1/object/public/doordash-carts/${cartUrl}`} 
                                      alt={`DoorDash Cart ${index + 1}`} 
                                      className="screenshot-preview" 
                                      style={{maxWidth: '120px', borderRadius: '4px', maxHeight: '80px', objectFit: 'cover'}} 
                                    />
                                  ))}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : null}
                      
                      {/* Display receipt image */}
                      {order.doordash_receipt_url && (
                        <div style={{marginTop: 8}}>
                          <div style={{fontSize: '0.9em', color: '#fff', marginBottom: 4}}>Receipt:</div>
                          <img 
                            src={`https://lazzdyaxyzqrzmmucwhv.supabase.co/storage/v1/object/public/screenshots/${order.doordash_receipt_url.replace(/^screenshots\//, '').replace(/^screenshots\//, '')}`} 
                            alt="DoorDash Receipt" 
                            className="screenshot-preview" 
                            style={{maxWidth: '120px', borderRadius: '4px', maxHeight: '80px', objectFit: 'cover'}} 
                          />
                        </div>
                      )}
                      {order.status === 'pending_payment' && (
                        <button 
                          onClick={() => navigate(`/payment?order_id=${order.id}`)}
                          style={{
                            background: '#ffe082',
                            color: '#23272f',
                            fontWeight: 600,
                            border: 'none',
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontSize: '0.9em',
                            cursor: 'pointer',
                            marginTop: 8
                          }}
                        >
                          Pay Now
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {activeTab === 'placeOrder' && (
              <div style={{maxWidth: 420, margin: '0 auto'}}>
                                <OrderForm user={user} onOrderPlaced={handleOrderPlaced} />
              </div>
            )}
            {activeTab === 'profile' && profile && (
              <div style={{maxWidth: 420, flex: 1}}>
                <h2>Your Profile</h2>
                {!editProfile ? (
                  <>
                    <table style={{width: '100%', marginBottom: 18, color: '#fff', fontSize: '1.08rem'}}>
                      <tbody>
                        {/* Only show name, email, phone here */}
                        <tr>
                          <td style={{fontWeight: 500, padding: '7px 0', color: '#fff', width: 160}}>Name</td>
                          <td style={{padding: '7px 0'}}>{profile.name || <span style={{color:'#aaa'}}>Not set</span>}</td>
                        </tr>
                        <tr>
                          <td style={{fontWeight: 500, padding: '7px 0', color: '#fff', width: 160}}>Email</td>
                          <td style={{padding: '7px 0'}}>{profile.email || <span style={{color:'#aaa'}}>Not set</span>}</td>
                        </tr>
                        <tr>
                          <td style={{fontWeight: 500, padding: '7px 0', color: '#fff', width: 160}}>Phone</td>
                          <td style={{padding: '7px 0'}}>{profile.phone || <span style={{color:'#aaa'}}>Not set</span>}</td>
                        </tr>
                      </tbody>
                    </table>
                    <button onClick={() => setEditProfile(true)} style={{marginTop: 0, background: 'none', color: '#fff', border: '1px solid #fff', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer'}}>Edit Profile</button>
                    {profileMsg && <div className="message">{profileMsg}</div>}
                  </>
                ) : (
                  <div style={{marginTop: 8}}>
                    {/* Only edit name and phone here */}
                    <div className="form-group">
                      <label htmlFor="name">Name</label>
                      <input id="name" name="name" value={profileForm.name || ''} onChange={handleProfileChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input id="phone" name="phone" value={profileForm.phone || ''} onChange={handleProfileChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <input id="email" name="email" value={profileForm.email || ''} disabled />
                    </div>
                    <div style={{display:'flex', gap:12, marginTop:18}}>
                      <button onClick={handleProfileSave} style={{background: 'transparent', color: '#fff', border: '1px solid #fff', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer'}}>Save</button>
                      <button style={{background: 'transparent', color: '#bbb', border: '1px solid #bbb', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer'}} onClick={() => { setEditProfile(false); setProfileForm(profile); setProfileMsg(''); }}>Cancel</button>
                    </div>
                    {profileMsg && <div className="message">{profileMsg}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <style>{`
          @media (max-width: 700px) {
            .dashboard-main-flex {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .address-delivery-container {
              position: static !important;
              left: unset !important;
              top: unset !important;
              transform: none !important;
              width: 100vw !important;
              min-width: 0 !important;
              max-width: 100vw !important;
              margin: 0 0 24px 0 !important;
              border-radius: 0 0 18px 18px !important;
              box-shadow: none !important;
              z-index: 1 !important;
            }
          }
        `}</style>
      </div>
    </>
  );
} 

